import React, { useState, useEffect, useContext } from "react";
import UserAppSurveyReadOnlyRow from "./UserAppSurveyReadOnlyRow";
import UserAppSurveyTableRow from "./UserAppSurveyTableRow";
import UserContext from "../contexts/UserContext";
import { useTranslation } from 'react-i18next';
import FetchUtil from "../utils/FetchUtil";
import "../css/ListUsers.css";
import { useNavigate } from "react-router-dom";

function UserAppSurveyTable({ key, setAppSurvey, loginId, companyId }) {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [add, setAdd] = useState(false);
	const [error, setError] = useState("");
	const [isUserAppSurveyLoading, setIsUserAppSurveyLoading] = useState(false);
	const [isAppSurveyNameLoading, setIsAppSurveyNameLoading] = useState(false);
	const [appSurveyNames, setAppSurveyNames] = useState([]);
	const [userAppSurveys, setUserAppSurveys] = useState([]);
	const [count, setCount] = useState(0);
	const closeText = t("page.close");
	const userCtx = useContext(UserContext);

	const initialValues = {
		loginId: loginId,
		companyId: companyId,
		appSurveyKey: 0,
		canRead: false,
		canWrite: false,
		canReview: false,
	};
	const [formValues, setFormValues] = useState(initialValues);

	const resetCounter = () => {
		setCount(prevCount => prevCount + 1);
	};

	let userCompanyId = (userCtx.role !== "SiteAdmin") ? userCtx.userCompanyId : 0;
	useEffect(() => {
		fetchAppSurveyNames();
		fetchUserAppSurveys();
	}, []);

	const handleClose = (event) => {
		setAppSurvey(false);
	};

	function fetchAppSurveyNames() {
		setError("");
		setIsAppSurveyNameLoading(true);
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", getAll: false
			}),
		};
		try {
			FetchUtil.fetchAPI("GetAppSurveyNames", "/Service/StaticHelper/GetAppSurveyNames", requestOptions, retrieveAppSurveyNameSuccess, retrieveAppSurveyNameFail);
		} catch (error) {
			setIsAppSurveyNameLoading(false);
			throw new Error(error.message);
		}
	}

	function retrieveAppSurveyNameFail(redirect) {
		setIsAppSurveyNameLoading(false);
		if (redirect) {
			navigate("/");
		}
		setError(t("error.getAppSurveyFailed"));
	}

	function retrieveAppSurveyNameSuccess(responseData) {
		let getAppSurveyNameResponse;
		if (responseData) {
			getAppSurveyNameResponse = responseData.getAppSurveyNameResponse;
			if (getAppSurveyNameResponse && getAppSurveyNameResponse.errorCode === "0") {
				setAppSurveyNames(getAppSurveyNameResponse.appSurveyList.slice())
			} else if (getAppSurveyNameResponse && getAppSurveyNameResponse.errorCode === "114") {
				navigate("/", {
					state: {
						errorMessage: t("error.sessionTimeout"),
					},
				});
			} else if (getAppSurveyNameResponse) {
				setError(t("error.error") + " " + getAppSurveyNameResponse.errorMessage);
			} else {
				setError(t("error.system.fetch"));
			}
		}
		setIsAppSurveyNameLoading(false);
	}

	function fetchUserAppSurveys() {
		setError("");
		if (!initialValues.companyId || initialValues.companyId === 0 || !initialValues.loginId) {
			return;
		}
		resetCounter();
		setIsUserAppSurveyLoading(true);
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", getAll: true, companyId: initialValues.companyId,
				loginId: initialValues.loginId, userCompanyId: userCompanyId
			}),
		};
		try {
			FetchUtil.fetchAPI("GetUserAppSurvey", "/Service/UserService/GetUserAppSurvey", requestOptions, retrieveUserAppSurveySuccess, retrieveUserAppSurveyFail);
		} catch (error) {
			setIsUserAppSurveyLoading(false);
			throw new Error(error.message);
		}
	}

	function retrieveUserAppSurveyFail(redirect) {
		setIsUserAppSurveyLoading(false);
		if (redirect) {
			navigate("/");
		}
		setError(t("error.userGetAppSurveyFailed"));
	}

	function retrieveUserAppSurveySuccess(responseData) {
		let fetchUserAppSurveysResponse;
		if (responseData) {
			fetchUserAppSurveysResponse = responseData.fetchUserAppSurveysResponse;
			if (fetchUserAppSurveysResponse && fetchUserAppSurveysResponse.errorCode === "0") {
				setUserAppSurveys(fetchUserAppSurveysResponse.appSurveyList.slice())
			} else if (fetchUserAppSurveysResponse && fetchUserAppSurveysResponse.errorCode === "114") {
				navigate("/", {
					state: {
						errorMessage: t("error.sessionTimeout"),
					},
				});
			} else if (fetchUserAppSurveysResponse) {
				setError(t("error.error") + " " + fetchUserAppSurveysResponse.errorMessage);
			} else {
				setError(t("error.system.fetch"));
			}
		}
		setIsUserAppSurveyLoading(false);
	}

	const handleDeleteClick = (event, userAppSurveyItem, completeCallback) => {
		event.preventDefault();
		deleteUserAppSurvey(userAppSurveyItem, completeCallback);
	};
	const deleteUserAppSurvey = (userAppSurveyItem, completeCallback) => {
		let action = 3;
		if (userAppSurveyItem.statusCode === 1) {
			action = 4;
		}
		let serverParams = {
			loginId: formValues.loginId,
			appSurveyKey: userAppSurveyItem.appSurveyKey,
			userCompanyId: userCompanyId,
			source: "web",
			action: action
		};
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(serverParams),
		};

		try {
			FetchUtil.fetchAPI("DeleteUserAppSurvey", "/Service/UserService/DeleteUserAppSurvey", requestOptions, deleteUserAppSurveySuccess, deleteUserAppSurveyFail, completeCallback);
		} catch (error) {
			setError(error.message);
			if (completeCallback) {
				completeCallback();
			}
		}
	};

	function deleteUserAppSurveyFail(redirect, completeCallback) {
		setError(t("error.userAppSurveyEditFailed"));
		if (completeCallback) {
			completeCallback();
		}
	}
	function deleteUserAppSurveySuccess(responseData, completeCallback) {
		let deleteUserAppSurveyResponse;
		if (responseData) {
			deleteUserAppSurveyResponse = responseData.deleteUserAppSurveyResponse;
			if (deleteUserAppSurveyResponse) {
				if (deleteUserAppSurveyResponse.errorCode === "0") {
					setAdd(false);
					refreshUserAppSurvey();
				} else {
					setError(t("error.error") + " " +
						deleteUserAppSurveyResponse.errorMessage
					);
				}
			}
		}
		if (completeCallback) {
			completeCallback();
		}
	}
	const addRowTable = () => {
		setError("");
		if (!add) {
			setAdd(true);
		} else {
			setError(t("error.only1RowAllowed"));
		}

	};
	const removeRowTable = () => {
		setError("");
		setAdd(false);
	};
	const handleSubmit = (action, setValues, completeCallback) => {
		setError("");
		let found = false;
		if (!formValues.loginId) {
			setError(t("error.userReqd"));
			if (completeCallback) {
				completeCallback();
			}
			return false;
		}
		if (action === 1) {
			if (userAppSurveys.length > 0) {
				userAppSurveys.map((userAppSurveyItem, index) => {
					if (userAppSurveyItem.appSurveyKey === setValues.appSurveyKey) {
						setError(t("error.userAppSurveyDup"));
						found = true;
						if (completeCallback) {
							completeCallback();
						}
						return false;
					}
				})
			}
			if (!found) {
				saveUserAppSurvey(action, setValues, completeCallback);
			} else {
				if (completeCallback) {
					completeCallback();
				}
			}
		} else if (action === 2) {
			if (userAppSurveys.length > 0) {
				userAppSurveys.map((userAppSurveyItem, index) => {
					if (userAppSurveyItem.appSurveyKey === setValues.appSurveyKey) {
						found = true;
					}
				})
			}
			if (found) {
				saveUserAppSurvey(action, setValues, completeCallback);
			} else {
				if (completeCallback) {
					completeCallback();
				}
			}
		} else if (completeCallback) {
			completeCallback();
		}
	};
	const saveUserAppSurvey = (action, setValues, completeCallback) => {
		let serverParams = {
			loginId: formValues.loginId,
			companyId: formValues.companyId,
			userCompanyId: userCompanyId,
			appSurveyKey: setValues.appSurveyKey,
			canRead: setValues.canRead,
			canWrite: setValues.canWrite,
			canReview: setValues.canReview,
			source: "web",
			action: action
		};
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(serverParams),
		};

		try {
			FetchUtil.fetchAPI("AddUpdateUserAppSurvey", "/Service/UserService/AddUpdateUserAppSurvey", requestOptions, saveUserAppSurveySuccess, saveUserAppSurveyFail, completeCallback);
		} catch (error) {
			setError(error.message);
			if (completeCallback) {
				completeCallback();
			}
		}
	};

	function saveUserAppSurveyFail(redirect, completeCallback) {
		setError(t("error.userAppSurveyEditFailed"));
		if (completeCallback) {
			completeCallback();
		}
	}
	function saveUserAppSurveySuccess(responseData, completeCallback) {
		let addUpdateUserAppSurveyResponse;
		if (responseData) {
			addUpdateUserAppSurveyResponse = responseData.addUpdateUserAppSurveyResponse;
			if (addUpdateUserAppSurveyResponse) {
				if (addUpdateUserAppSurveyResponse.errorCode === "0") {
					setAdd(false);
					refreshUserAppSurvey();
				} else {
					setError(t("error.error") + " " +
						addUpdateUserAppSurveyResponse.errorMessage
					);
				}
			}
		}
		if (completeCallback) {
			completeCallback();
		}
	}
	function refreshUserAppSurvey() {
		fetchUserAppSurveys();
	}
	return (
		<>
			{(userCtx.role === "SiteAdmin" || userCtx.role === "ClientAdmin") ?
				<div className="container-fluid container_list_sm viewh-85">
					<div className="row">
						<div className="col mt-3">
							{(error) ? (
								<div id="inerrorMessage" className="errorblock">
									{error}
								</div>
							) : (<></>)}
						</div>
						{(isUserAppSurveyLoading || isAppSurveyNameLoading) && (<div> <span class="spinner-border spinner-border-sm spinner-lg-size"></span> </div>)}
					</div>
					<div className="row" style={{ margin: "0px" }}>
						<div className="col">
							<a class="popup-close" id="boxclose" onClick={(e) => handleClose(e)} ></a>
						</div>
					</div>
					<div className="row">
						<div className="col">
							<div id="placeholder_1" className="placeholder-pg"></div>
						</div>
					</div>
					<div className="row">
						<div className="col">
							<table className="list-table" role="table">
								<thead>
									<tr role="row">
										<th className="list-th">{t("page.app.name")}</th>
										<th className="list-th">{t("page.survey.name")}</th>
										<th className="list-th">{t("page.read")}</th>
										<th className="list-th">{t("page.write")}</th>
										<th className="list-th">{t("page.review")}</th>
										<th className="list-th">{t("page.status")}</th>
										<th className="list-th" style={{ maxWidth: "9vw" }}>
											<button className="btn btn-primary btn-sm me-2 mb-1" onClick={addRowTable}>
												{t("page.addRow")}
											</button>&nbsp;&nbsp;
											<button className="btn btn-primary btn-sm me-2 mb-1" onClick={removeRowTable}>
												{t("page.delRow")}
											</button>
										</th>
									</tr>
								</thead>
								<tbody role="rowgroup">
									{add &&
										<UserAppSurveyTableRow
											index={count}
											appSurveyNames={appSurveyNames}
											handleSubmit={handleSubmit}
											setError={setError}
											t={t}
											setAdd={setAdd}
											oddIdx={((userAppSurveys.length % 2) > 0) ? true : false} />
									}
									{(userAppSurveys.length > 0) ? (
										userAppSurveys.map((userAppSurveyItem, index) => (
											<UserAppSurveyReadOnlyRow
												index={count}
												t={t}
												userAppSurveyItem={userAppSurveyItem}
												handleDeleteClick={handleDeleteClick}
												oddIdx={((index % 2) > 0) ? true : false}
												add={add}
												setError={setError}
												handleSubmit={handleSubmit}
											/>
										))
									) : (<></>)}
								</tbody>
							</table>
							<div className="row">
								<div className="col mt-3">
									{(error) ? (
										<div id="inerrorMessage1" className="errorblock">
											{error}
										</div>
									) : (<></>)}
								</div>
							</div>
							<div className="footer">
								<input
									type="button"
									name="edit-user-appsurvey-close"
									id="edit-user-appsurvey-close"
									className="btn btn-secondary btn-block" data-dismiss="modal"
									onClick={(e) => handleClose(e)}
									style={{ marginTop: "10px", marginBottom: "10px" }}
									value={closeText}
								></input>
							</div>
						</div>
					</div>
				</div>
				: null}
		</>
	);
}
export default UserAppSurveyTable;